/**
 * InfoAbscheiderPage – Digitales Betriebstagebuch für Abscheider-Anlagen
 *
 * Drei Eintragstypen:
 *  A – Monatliche Eigenkontrolle  (intern, cm-Stände, auto-fill Prüfer)
 *  B – Fachkundigenprüfung        (extern, cm-Stände + Mängel, lila Highlight)
 *  C – Entleerung & Wartung       (Entsorger, m³-Mengen, teal)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  FlaskConical, Plus, Trash2, Camera, File, Loader2,
  ChevronDown, ChevronUp, CheckCircle2, X, User, Shield, Wrench,
  FileDown, PenLine, RotateCcw, Pencil,
} from 'lucide-react';
import SignaturePad from '../../components/ui/SignaturePad';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Typen-Konfiguration ────────────────────────────────────────────────────
const ENTRY_TYPES = {
  A: {
    label: 'Eigenkontrolle',
    sublabel: 'Monatlich / Intern',
    icon: User,
    color: 'gray',
    badgeCls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    activeCls: 'border-gray-400 bg-gray-100 text-gray-800 dark:border-gray-400 dark:bg-gray-700/60 dark:text-gray-200',
    cardCls: 'border-l-gray-400 dark:border-l-gray-500',
  },
  B: {
    label: 'Fachkundigenprüfung',
    sublabel: 'Extern / Sachverständiger',
    icon: Shield,
    color: 'violet',
    badgeCls: 'bg-violet-100 text-violet-800 dark:bg-violet-700/60 dark:text-violet-200',
    activeCls: 'border-violet-500 bg-violet-50 text-violet-800 dark:border-violet-400 dark:bg-violet-800/50 dark:text-violet-200',
    cardCls: 'border-l-violet-500 dark:border-l-violet-400',
  },
  C: {
    label: 'Entleerung & Wartung',
    sublabel: 'Entsorgungsfachbetrieb',
    icon: Wrench,
    color: 'teal',
    badgeCls: 'bg-teal-100 text-teal-800 dark:bg-teal-700/60 dark:text-teal-200',
    activeCls: 'border-teal-500 bg-teal-50 text-teal-800 dark:border-teal-400 dark:bg-teal-800/50 dark:text-teal-200',
    cardCls: 'border-l-teal-500 dark:border-l-teal-400',
  },
};

// m³-Gruppen für Typ C
const MENGEN_GROUPS = [
  { label: 'Schlammfang (SF)', abbrev: 'SF', fields: ['sf1_m3', 'sf2_m3', 'sf3_m3'], labels: ['SF 1', 'SF 2', 'SF 3'] },
  { label: 'Benzinabscheider (BA)', abbrev: 'BA', fields: ['ba1_m3', 'ba2_m3', 'ba3_m3'], labels: ['BA 1', 'BA 2', 'BA 3'] },
  { label: 'Koaleszenzabscheider (KA)', abbrev: 'KA', fields: ['ka1_m3', 'ka2_m3'], labels: ['KA 1', 'KA 2'] },
];
const ALL_M3_FIELDS = MENGEN_GROUPS.flatMap(g => g.fields);

// cm-Felder für Typ A & B
const CM_FIELDS = [
  { field: 'sf_schlamm_cm', label: 'SF – Schlamm (cm)', placeholder: 'z.B. 12' },
  { field: 'ba_oel_cm',     label: 'BA – Ölschicht (cm)', placeholder: 'z.B. 3' },
  { field: 'ka_oel_cm',     label: 'KA – Ölschicht (cm)', placeholder: 'z.B. 1' },
];


// ── Ja/Nein Toggle Komponente ─────────────────────────────────────────────
function JaNeinToggle({ label, value, onChange, testId, compact = false }) {
  return (
    <div className={`flex items-center justify-between ${compact ? 'p-2.5' : 'p-3'} rounded-lg border transition-colors ${
      value === true  ? 'border-green-300 bg-green-50 dark:bg-green-900/20' :
      value === false ? 'border-red-200 bg-red-50 dark:bg-red-900/10' :
                        'border-border'
    }`} data-testid={testId}>
      <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-foreground`}>{label}</span>
      <div className="flex gap-1.5 shrink-0">
        {[true, false].map(v => (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(value === v ? null : v)}
            className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-all ${
              value === v
                ? v === true
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'bg-red-500 border-red-500 text-white'
                : 'bg-background border-border text-muted-foreground hover:border-muted-foreground'
            }`}
            data-testid={`${testId}-${v ? 'ja' : 'nein'}`}
          >
            {v ? 'Ja' : 'Nein'}
          </button>
        ))}
      </div>
    </div>
  );
}


const emptyForm = (userName = '') => ({
  entry_type: 'A',
  performed_by: userName,
  emptying_date: new Date().toISOString().slice(0, 10),
  // cm-Felder (A & B)
  sf_schlamm_cm: '', ba_oel_cm: '', ka_oel_cm: '',
  // m³-Felder (C)
  sf1_m3: '', sf2_m3: '', sf3_m3: '',
  ba1_m3: '', ba2_m3: '', ba3_m3: '',
  ka1_m3: '', ka2_m3: '',
  // Status
  funktion_io: null,        // null = nicht gesetzt, true = Ja, false = Nein
  ba_ka_aufgefuellt: null,
  // "Bitte ankreuzen" – Ja/Nein Toggles
  pruefung_selbsttaetiger_verschluss: null,
  pruefung_alarm: null,
  ablauf_kontrolle: null,
  bauzustand_sf: null,
  bauzustand_ba_ka: null,
  schwimmstoffe_sf: null,
  schwimmstoffe_ba_ka: null,
  betriebstagebuch_vorhanden: null,
  betriebstagebuch_gefuehrt: null,
  tsu_eingewiesen: null,
  eigenkontrolle_durchgefuehrt: null,
  // Texte
  maengel: '',
  notizen: '',
  // Unterschriften (base64)
  unterschrift_person: null,
  unterschrift_firma: null,
});

const fmtDate = (iso) => {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return iso?.slice(0, 10); }
};
const fmtDateTime = (iso) => {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function InfoAbscheiderPage() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const isAdmin = ['admin', 'superadmin', 'stationsleitung'].includes(user?.role);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [module, setModule] = useState(null);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);

  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [existingDoc, setExistingDoc] = useState(null); // {path,type} beim Bearbeiten
  const [existingSig, setExistingSig] = useState({ person: null, firma: null });
  const [form, setForm] = useState(emptyForm(user?.name || ''));
  const [dialogLocId, setDialogLocId] = useState('');
  const [dokument, setDokument] = useState(null);
  const [dokumentPreview, setDokumentPreview] = useState(null);
  const [dokumentType, setDokumentType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null); // Vollbild-Dokumentenviewer
  const [expandedId, setExpandedId] = useState(null);
  const fileRef = useRef();
  const sigPersonRef = useRef();
  const sigFirmaRef = useRef();

  // PDF-Export State
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportVon, setExportVon] = useState('');
  const [exportBis, setExportBis] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const params = selectedLocation ? { location_id: selectedLocation } : {};
      const [mRes, eRes, lRes] = await Promise.all([
        axios.get(`${API}/api/info/modules`, { headers }),
        axios.get(`${API}/api/info/${moduleId}/entries`, { headers, params }),
        axios.get(`${API}/api/user/locations`, { headers }),
      ]);
      const mod = (mRes.data || []).find(m => m.id === moduleId);
      setModule(mod || null);
      setEntries(eRes.data || []);
      const locs = lRes.data || [];
      setLocations(locs);
      if (!selectedLocation && locs.length > 0) {
        setSelectedLocation(locs[0].id);
        setDialogLocId(locs[0].id);
      }
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, [moduleId, selectedLocation]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const locName = (id) => locations.find(l => l.id === id)?.name || id || '–';

  const canWrite = isAdmin || (user?.role === 'employee' && module?.employee_can_add);

  const openCreate = () => {
    setEditingId(null);
    setExistingDoc(null);
    setExistingSig({ person: null, firma: null });
    setForm(emptyForm(user?.name || ''));
    setDokument(null); setDokumentPreview(null); setDokumentType(null);
    setDialogLocId(selectedLocation || locations[0]?.id || '');
    setTimeout(() => { sigPersonRef.current?.clear(); sigFirmaRef.current?.clear(); }, 100);
    setShowDialog(true);
  };

  const openEdit = (entry) => {
    const rd = entry.row_data || {};
    // Prefill: Zahlen → String für Inputs, null → Default; Unterschriften bleiben erhalten
    const prefill = { ...emptyForm(user?.name || '') };
    Object.keys(rd).forEach(k => {
      const v = rd[k];
      if (v === null || v === undefined) return;
      prefill[k] = (typeof v === 'number') ? String(v) : v;
    });
    setEditingId(entry.id);
    setForm(prefill);
    setDialogLocId(entry.location_id);
    setExistingSig({ person: rd.unterschrift_person || null, firma: rd.unterschrift_firma || null });
    setExistingDoc(entry.dokument_path ? { path: entry.dokument_path, type: entry.dokument_type } : null);
    setDokument(null); setDokumentPreview(null); setDokumentType(null);
    setTimeout(() => { sigPersonRef.current?.clear(); sigFirmaRef.current?.clear(); }, 100);
    setShowDialog(true);
  };

  // Typ wechseln → Prüfer-Feld anpassen
  const changeType = (type) => {
    setForm(f => ({
      ...f,
      entry_type: type,
      performed_by: type === 'A' ? (user?.name || '') : (f.entry_type === 'A' ? '' : f.performed_by),
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDokument(file); setDokumentType(file.type);
    setDokumentPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
  };

  const handleSave = async () => {
    const type = form.entry_type;
    if (!form.emptying_date) { toast.error('Datum ist Pflichtfeld'); return; }
    if (!form.performed_by.trim()) { toast.error('Prüfer / Firma ist Pflichtfeld'); return; }

    if (type === 'C') {
      const hasM3 = ALL_M3_FIELDS.some(f => form[f] !== '' && !isNaN(parseFloat(form[f])));
      if (!hasM3) { toast.error('Mindestens eine Mengenangabe (SF, BA oder KA) ist erforderlich'); return; }
    }

    const locId = dialogLocId || locations[0]?.id;
    if (!locId) { toast.error('Bitte Standort wählen'); return; }

    setSaving(true);
    try {
      const rowData = { ...form };
      // Zahlenfelder bereinigen
      ALL_M3_FIELDS.forEach(f => {
        rowData[f] = (form[f] !== '' && !isNaN(parseFloat(form[f]))) ? parseFloat(form[f]) : null;
      });
      ['sf_schlamm_cm', 'ba_oel_cm', 'ka_oel_cm'].forEach(f => {
        const v = form[f];
        if (v === '' || v == null) { rowData[f] = null; return; }
        // Zahlen direkt → float; Text mit < oder > → als String speichern (z.B. "< 5")
        const num = parseFloat(v);
        rowData[f] = !isNaN(num) && String(num) === v.trim() ? num : v.trim();
      });
      // Unterschriften als base64 speichern
      if (sigPersonRef.current && !sigPersonRef.current.isEmpty()) {
        rowData.unterschrift_person = sigPersonRef.current.toDataURL('image/png');
      }
      if (type === 'B' && sigFirmaRef.current && !sigFirmaRef.current.isEmpty()) {
        rowData.unterschrift_firma = sigFirmaRef.current.toDataURL('image/png');
      }

      let entryId = editingId;
      if (editingId) {
        await axios.patch(
          `${API}/api/info/${moduleId}/abscheider-entries/${editingId}`,
          { row_data: rowData },
          { headers }
        );
      } else {
        const res = await axios.post(
          `${API}/api/info/${moduleId}/entries`,
          { location_id: locId, row_data: rowData },
          { headers }
        );
        entryId = res.data.id;
      }

      if (dokument && entryId) {
        const fd = new FormData();
        fd.append('file', dokument);
        await axios.post(
          `${API}/api/info/${moduleId}/abscheider-dokument?entry_id=${entryId}`,
          fd,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
      }

      const typeLabel = ENTRY_TYPES[type]?.label;
      toast.success(editingId ? `${typeLabel} aktualisiert!` : `${typeLabel} gespeichert!`);
      setShowDialog(false);
      setEditingId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/info/${moduleId}/entries/${id}`, { headers });
      toast.success('Gelöscht'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedLocation) params.append('location_id', selectedLocation);
      if (exportVon) params.append('datum_von', exportVon);
      if (exportBis) params.append('datum_bis', exportBis);

      const res = await axios.get(
        `${API}/api/info/${moduleId}/abscheider-export-pdf?${params}`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const loc = locations.find(l => l.id === selectedLocation);
      a.download = `Betriebstagebuch_Abscheider_${loc?.name || 'alle'}_${exportVon || 'alle'}_${exportBis || 'heute'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF erfolgreich exportiert!');
      setShowExportDialog(false);
    } catch (e) {
      toast.error('PDF-Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    </DashboardLayout>
  );

  const filteredEntries = selectedLocation
    ? entries.filter(e => e.location_id === selectedLocation)
    : entries;

  // Zähle Typen für den Header
  const countByType = (t) => filteredEntries.filter(e => e.row_data?.entry_type === t || (!e.row_data?.entry_type && t === 'C')).length;

  return (
    <>
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-5 p-4 md:p-6" data-testid="abscheider-page">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
              <FlaskConical className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{module?.name || 'Abscheider-Anlage'}</h1>
              <p className="text-sm text-muted-foreground">Digitales Betriebstagebuch</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Mini-Statistik */}
            {['A', 'B', 'C'].map(t => (
              <span key={t} className={`text-xs px-2 py-1 rounded-full font-medium ${ENTRY_TYPES[t].badgeCls}`}>
                {ENTRY_TYPES[t].label.split(' ')[0]} · {countByType(t)}
              </span>
            ))}
            {isAdmin && locations.length > 1 && (
              <select
                value={selectedLocation}
                onChange={e => setSelectedLocation(e.target.value)}
                className="h-8 rounded-md border bg-background px-2 text-xs"
              >
                <option value="">Alle Standorte</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            {isAdmin && (
              <Button
                size="sm" variant="outline"
                onClick={() => { setExportVon(''); setExportBis(''); setShowExportDialog(true); }}
                data-testid="export-pdf-btn"
                className="gap-1.5"
              >
                <FileDown className="h-4 w-4" />
                PDF Export
              </Button>
            )}
            <Button size="sm" onClick={openCreate} data-testid="add-eintrag-btn">
              <Plus className="h-4 w-4 mr-1" /> Eintrag erfassen
            </Button>
          </div>
        </div>

        {/* Eintrags-Liste */}
        {filteredEntries.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-14 text-center space-y-3">
              <FlaskConical className="h-12 w-12 text-muted-foreground/30 mx-auto" />
              <p className="text-muted-foreground font-medium">Noch keine Einträge vorhanden</p>
              <Button onClick={openCreate} variant="outline">
                <Plus className="h-4 w-4 mr-2" /> Ersten Eintrag erfassen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2.5">
            {filteredEntries.map(entry => {
              const rd = entry.row_data || {};
              const type = rd.entry_type || 'C';
              const cfg = ENTRY_TYPES[type] || ENTRY_TYPES.C;
              const TypeIcon = cfg.icon;
              const isExpanded = expandedId === entry.id;
              const performedBy = rd.performed_by || rd.disposal_company || '–';

              return (
                <Card
                  key={entry.id}
                  className={`shadow-sm border-l-4 ${cfg.cardCls}`}
                  data-testid={`entry-${entry.id}`}
                >
                  <CardContent className="p-0">
                    <button
                      className="w-full p-4 text-left"
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Typ-Badge */}
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-lg shrink-0 ${cfg.badgeCls}`}>
                          {type}
                        </span>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-foreground">
                              {fmtDate(rd.emptying_date)}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badgeCls}`}>
                              {cfg.label}
                            </span>
                            {rd.funktion_io && (
                              <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full">Funktion i.O.</span>
                            )}
                            {(rd.maengel && rd.maengel.trim()) && (
                              <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">Mängel</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                            <TypeIcon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{performedBy}</span>
                            {entry.dokument_path && <span>· Dokument</span>}
                          </p>
                        </div>

                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                      </div>
                    </button>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">

                        {/* cm-Felder (A & B) */}
                        {(type === 'A' || type === 'B') && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">cm-Stände</p>
                            <div className="grid grid-cols-3 gap-2">
                              {CM_FIELDS.map(({ field, label }) => (
                                <div key={field} className="bg-muted/40 rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-muted-foreground">{label.split(' – ')[0]}</p>
                                  <p className="font-mono font-semibold text-sm">
                                    {rd[field] != null
                                      ? typeof rd[field] === 'string' && rd[field].includes('<')
                                        ? rd[field]          // z.B. "< 5" – kein "cm" anhängen
                                        : `${rd[field]} cm`
                                      : '–'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* m³-Felder (C) */}
                        {type === 'C' && (
                          <div className="space-y-3">
                            {MENGEN_GROUPS.map(group => {
                              const vals = group.fields.filter(f => rd[f] != null);
                              if (vals.length === 0) return null;
                              return (
                                <div key={group.abbrev}>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{group.label}</p>
                                  <div className="grid grid-cols-3 gap-2">
                                    {group.fields.map((field, i) => (
                                      <div key={field} className="bg-muted/40 rounded-lg px-3 py-2 text-center">
                                        <p className="text-xs text-muted-foreground">{group.labels[i]}</p>
                                        <p className="font-mono font-semibold text-sm">
                                          {rd[field] != null ? `${rd[field]} m³` : '–'}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Status */}
                        <div className="flex flex-wrap gap-2">
                          {rd.funktion_io != null && (
                            <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${rd.funktion_io ? 'bg-green-100 text-green-700 dark:bg-green-900/30' : 'bg-red-100 text-red-700'}`}>
                              <CheckCircle2 className="h-3 w-3" /> Funktion i.O.: {rd.funktion_io ? 'Ja' : 'Nein'}
                            </span>
                          )}
                          {rd.ba_ka_aufgefuellt != null && (
                            <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${rd.ba_ka_aufgefuellt ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' : 'bg-muted text-muted-foreground'}`}>
                              <CheckCircle2 className="h-3 w-3" /> BA/KA aufgefüllt: {rd.ba_ka_aufgefuellt ? 'Ja' : 'Nein'}
                            </span>
                          )}
                        </div>

                        {/* Bitte ankreuzen – Zusammenfassung */}
                        {[
                          { key: 'pruefung_selbsttaetiger_verschluss', label: 'Prüfung selbsttätiger Verschluss' },
                          { key: 'pruefung_alarm',     label: 'Prüfung Alarmanlage' },
                          { key: 'ablauf_kontrolle',   label: 'Ablaufkontrolle' },
                          { key: 'bauzustand_sf',      label: 'Bauzustand SF' },
                          { key: 'bauzustand_ba_ka',   label: 'Bauzustand BA/KA' },
                          { key: 'schwimmstoffe_sf',   label: 'Schwimmstoffe SF' },
                          { key: 'schwimmstoffe_ba_ka', label: 'Schwimmstoffe BA/KA' },
                          { key: 'betriebstagebuch_vorhanden',   label: 'Betriebstagebuch vorhanden' },
                          { key: 'betriebstagebuch_gefuehrt',    label: 'Betriebstagebuch geführt' },
                          { key: 'tsu_eingewiesen',              label: 'TSU eingewiesen' },
                          { key: 'eigenkontrolle_durchgefuehrt', label: 'Eigenkontrolle durchgeführt' },
                        ].filter(({ key }) => rd[key] != null).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Bitte ankreuzen</p>
                            <div className="grid grid-cols-2 gap-1.5">
                              {[
                                { key: 'pruefung_selbsttaetiger_verschluss', label: 'Selbsttätiger Verschluss' },
                                { key: 'pruefung_alarm',     label: 'Alarmanlage' },
                                { key: 'ablauf_kontrolle',   label: 'Ablaufkontrolle' },
                                { key: 'bauzustand_sf',      label: 'Bauzustand SF' },
                                { key: 'bauzustand_ba_ka',   label: 'Bauzustand BA/KA' },
                                { key: 'schwimmstoffe_sf',   label: 'Schwimmstoffe SF' },
                                { key: 'schwimmstoffe_ba_ka', label: 'Schwimmstoffe BA/KA' },
                                { key: 'betriebstagebuch_vorhanden',   label: 'Betr.tagebuch vorhanden' },
                                { key: 'betriebstagebuch_gefuehrt',    label: 'Betr.tagebuch geführt' },
                                { key: 'tsu_eingewiesen',              label: 'TSU eingewiesen' },
                                { key: 'eigenkontrolle_durchgefuehrt', label: 'Eigenkontrolle' },
                              ].filter(({ key }) => rd[key] != null).map(({ key, label }) => (
                                <div key={key} className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs ${
                                  rd[key] ? 'bg-green-50 text-green-700 dark:bg-green-900/20' : 'bg-red-50 text-red-600 dark:bg-red-900/10'
                                }`}>
                                  <span>{label}</span>
                                  <span className="font-bold">{rd[key] ? 'Ja' : 'Nein'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Unterschriften */}
                        {(rd.unterschrift_person || rd.unterschrift_firma) && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Unterschriften</p>
                            <div className="grid grid-cols-2 gap-3">
                              {rd.unterschrift_person && (
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Ausfüllende Person</p>
                                  <img src={rd.unterschrift_person} alt="Unterschrift" className="border rounded-lg bg-white w-full max-h-16 object-contain" />
                                </div>
                              )}
                              {rd.unterschrift_firma && (
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Externe Firma</p>
                                  <img src={rd.unterschrift_firma} alt="Unterschrift Firma" className="border border-violet-200 rounded-lg bg-white w-full max-h-16 object-contain" />
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Mängel (B) */}
                        {type === 'B' && rd.maengel && (
                          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
                            <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">Mängel / Hinweise des Prüfers</p>
                            <p className="text-sm text-foreground">{rd.maengel}</p>
                          </div>
                        )}

                        {/* Notizen */}
                        {rd.notizen && (
                          <div className="bg-muted/40 rounded-lg p-3">
                            <p className="text-xs text-muted-foreground mb-1">Notizen</p>
                            <p className="text-sm text-foreground">{rd.notizen}</p>
                          </div>
                        )}

                        {/* Dokument */}
                        {entry.dokument_path && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dokument / Protokoll</p>
                            {(() => {
                              const tok = localStorage.getItem('token');
                              const url = `${API}/api/info/${moduleId}/abscheider-dokument?entry_id=${entry.id}&token=${tok}`;
                              return entry.dokument_type?.startsWith('image/') ? (
                                <button
                                  type="button"
                                  onClick={() => setViewerUrl(url)}
                                  className="block w-full text-left"
                                >
                                  <img
                                    src={url}
                                    alt="Messprotokoll"
                                    className="rounded-lg max-h-40 w-full object-contain border bg-white dark:bg-gray-900"
                                  />
                                  <p className="text-xs text-primary mt-1">Tippen zum Vergrößern</p>
                                </button>
                              ) : (
                                <a
                                  href={url}
                                  target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                                >
                                  <File className="h-4 w-4" /> Dokument öffnen
                                </a>
                              );
                            })()}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border">
                          <span>
                            Erfasst von {entry.created_by_name} · {fmtDateTime(entry.created_at)}
                            {entry.updated_at && entry.updated_by_name && (
                              <span className="block text-amber-600 dark:text-amber-400">
                                Bearbeitet von {entry.updated_by_name} · {fmtDateTime(entry.updated_at)}
                              </span>
                            )}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            {canWrite && (
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => openEdit(entry)}
                                className="h-7 w-7 text-primary hover:text-primary hover:bg-primary/10"
                                data-testid={`edit-entry-${entry.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => handleDelete(entry.id)}
                                className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={`delete-entry-${entry.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Export-Dialog ────────────────────────────────────────────────── */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-sm" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-4 w-4 text-teal-600" />
              PDF Export – Betriebstagebuch
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Wähle den Zeitraum für den Export. Leer lassen für alle Einträge.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Von</label>
                <Input
                  type="date"
                  value={exportVon}
                  onChange={e => setExportVon(e.target.value)}
                  data-testid="export-von-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Bis</label>
                <Input
                  type="date"
                  value={exportBis}
                  onChange={e => setExportBis(e.target.value)}
                  data-testid="export-bis-input"
                />
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
              <p>Das PDF enthält:</p>
              <p>· Alle Einträge tabellarisch (A / B / C)</p>
              <p>· Foto-Anhänge auf separater Seite</p>
              <p>· Unterschriftsfeld auf der letzten Seite</p>
              <p>· DIN 1999-100 / DIN 4040-100 Referenz</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Abbrechen</Button>
            <Button
              onClick={handleExportPdf}
              disabled={exporting}
              data-testid="confirm-export-btn"
              className="gap-2"
            >
              {exporting
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Generiere PDF…</>
                : <><FileDown className="h-4 w-4" /> PDF herunterladen</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Erfassungs-Dialog ─────────────────────────────────────────────── */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="abscheider-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-teal-600" />
              {editingId ? 'Eintrag bearbeiten' : 'Eintrag erfassen'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* ── Typ-Auswahl ── */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Art des Eintrags *</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(ENTRY_TYPES).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  const active = form.entry_type === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => changeType(key)}
                      data-testid={`type-${key}-btn`}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-xs font-medium transition-all ${
                        active
                          ? cfg.activeCls
                          : 'border-border text-muted-foreground hover:border-muted-foreground/50 dark:border-border dark:hover:border-muted-foreground/40'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-center leading-tight">{cfg.label}</span>
                      <span className={`text-[10px] opacity-70 ${active ? '' : 'text-muted-foreground'}`}>{cfg.sublabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Standort (Admin) ── */}
            {isAdmin && locations.length > 1 && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Standort *</label>
                <select
                  value={dialogLocId}
                  onChange={e => setDialogLocId(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  data-testid="location-select"
                >
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}

            {/* ── Datum ── */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Datum *</label>
              <Input
                type="date"
                value={form.emptying_date}
                onChange={e => setForm(f => ({ ...f, emptying_date: e.target.value }))}
                data-testid="date-input"
              />
            </div>

            {/* ── Prüfer / Firma ── */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {form.entry_type === 'C' ? 'Entsorgungsfachbetrieb *' : 'Prüfer / Firma *'}
              </label>
              <Input
                placeholder={form.entry_type === 'A' ? 'Automatisch aus deinem Account' : 'z.B. TÜV Rheinland oder Müller Entsorgung GmbH'}
                value={form.performed_by}
                onChange={e => setForm(f => ({ ...f, performed_by: e.target.value }))}
                data-testid="performed-by-input"
              />
              {form.entry_type === 'A' && (
                <p className="text-xs text-muted-foreground">Automatisch aus deinem Account. Editierbar falls abweichend.</p>
              )}
            </div>

            {/* ── cm-Stände (A & B) ── */}
            {(form.entry_type === 'A' || form.entry_type === 'B') && (
              <div className="space-y-2">
                <label className="text-sm font-medium">cm-Stände</label>
                <div className="grid grid-cols-3 gap-3">
                  {CM_FIELDS.map(({ field, label, placeholder }) => {
                    const hasLt = String(form[field]).startsWith('<');
                    const numVal = hasLt ? String(form[field]).replace(/^<\s*/, '') : String(form[field] ?? '');
                    return (
                      <div key={field} className="space-y-1">
                        <label className="text-xs text-muted-foreground">{label}</label>
                        <div className="flex gap-1">
                          {/* < Taste */}
                          <button
                            type="button"
                            title="Kleiner-als Zeichen einfügen"
                            onClick={() => setForm(f => ({
                              ...f,
                              [field]: hasLt
                                ? numVal           // < entfernen
                                : `< ${numVal}`,   // < hinzufügen
                            }))}
                            className={`h-8 w-8 shrink-0 rounded-md border text-sm font-bold transition-colors ${
                              hasLt
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'border-border text-muted-foreground bg-background hover:bg-muted'
                            }`}
                            data-testid={`${field}-lt-btn`}
                          >
                            &lt;
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder={placeholder}
                            value={numVal}
                            onChange={e => setForm(f => ({
                              ...f,
                              [field]: hasLt ? `< ${e.target.value}` : e.target.value,
                            }))}
                            className="font-mono text-sm h-8 w-full rounded-md border border-input bg-background px-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            data-testid={`${field}-input`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Tipp: <code className="bg-muted px-1 rounded">&lt;</code>-Taste drücken für Werte wie „&lt; 5 cm"
                </p>
              </div>
            )}

            {/* ── m³-Mengen (C) ── */}
            {form.entry_type === 'C' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Abgefahrene Mengen (m³)</label>
                  <span className="text-xs text-muted-foreground">– mind. 1 Feld</span>
                </div>
                {MENGEN_GROUPS.map(group => (
                  <div key={group.abbrev} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{group.label}</p>
                    <div className={`grid gap-2 ${group.fields.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                      {group.fields.map((field, i) => (
                        <div key={field} className="space-y-1">
                          <label className="text-xs text-muted-foreground">{group.labels[i]}</label>
                          <Input
                            type="number" step="0.001" min="0"
                            placeholder="0.000"
                            value={form[field]}
                            onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                            className="font-mono text-sm h-8"
                            data-testid={`${field}-input`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                  Tipp: Gesamtmengen in SF 1 / BA 1 / KA 1 eintragen wenn keine Einzelzuordnung möglich.
                </p>
              </div>
            )}

            {/* ── Status-Checks ── */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Status-Prüfung</label>
              {[
                { field: 'funktion_io', label: 'Anlage Funktion i.O.?' },
                { field: 'ba_ka_aufgefuellt', label: 'BA / KA mit Wasser aufgefüllt?' },
              ].map(({ field, label }) => (
                <JaNeinToggle key={field} label={label} value={form[field]}
                  onChange={v => setForm(f => ({ ...f, [field]: v }))}
                  testId={`${field}-toggle`} />
              ))}
            </div>

            {/* ── Bitte ankreuzen ── */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border" />
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide shrink-0">
                  Bitte ankreuzen
                </label>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Linke Spalte: einfache Ja/Nein */}
              <div className="space-y-2">
                {[
                  { field: 'pruefung_selbsttaetiger_verschluss', label: 'Prüfung selbsttätiger Verschluss' },
                  { field: 'pruefung_alarm',     label: 'Prüfung Alarmanlage' },
                  { field: 'ablauf_kontrolle',   label: 'Ablaufkontrolle' },
                ].map(({ field, label }) => (
                  <JaNeinToggle key={field} label={label} value={form[field]}
                    onChange={v => setForm(f => ({ ...f, [field]: v }))}
                    testId={`${field}-toggle`} />
                ))}
              </div>

              {/* Bauzustand – SF + BA/KA separat */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Bauzustand</p>
                <div className="grid grid-cols-2 gap-2">
                  <JaNeinToggle label="SF" value={form.bauzustand_sf}
                    onChange={v => setForm(f => ({ ...f, bauzustand_sf: v }))}
                    testId="bauzustand-sf-toggle" compact />
                  <JaNeinToggle label="BA / KA" value={form.bauzustand_ba_ka}
                    onChange={v => setForm(f => ({ ...f, bauzustand_ba_ka: v }))}
                    testId="bauzustand-baka-toggle" compact />
                </div>
              </div>

              {/* Schwimmstoffe – SF + BA/KA separat */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Schwimmstoffe</p>
                <div className="grid grid-cols-2 gap-2">
                  <JaNeinToggle label="SF" value={form.schwimmstoffe_sf}
                    onChange={v => setForm(f => ({ ...f, schwimmstoffe_sf: v }))}
                    testId="schwimmstoffe-sf-toggle" compact />
                  <JaNeinToggle label="BA / KA" value={form.schwimmstoffe_ba_ka}
                    onChange={v => setForm(f => ({ ...f, schwimmstoffe_ba_ka: v }))}
                    testId="schwimmstoffe-baka-toggle" compact />
                </div>
              </div>

              {/* Rechte Seite: weitere Ja/Nein */}
              <div className="space-y-2">
                {[
                  { field: 'betriebstagebuch_vorhanden',  label: 'Betriebstagebuch vorhanden' },
                  { field: 'betriebstagebuch_gefuehrt',   label: 'Betriebstagebuch geführt' },
                  { field: 'tsu_eingewiesen',             label: 'TSU (Tankstellenunternehmer) eingewiesen' },
                  { field: 'eigenkontrolle_durchgefuehrt', label: 'Eigenkontrolle durchgeführt' },
                ].map(({ field, label }) => (
                  <JaNeinToggle key={field} label={label} value={form[field]}
                    onChange={v => setForm(f => ({ ...f, [field]: v }))}
                    testId={`${field}-toggle`} />
                ))}
              </div>
            </div>

            {/* ── Mängel-Feld (B) ── */}
            {form.entry_type === 'B' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Mängel / Hinweise des Prüfers</label>
                <textarea
                  placeholder="Festgestellte Mängel, Empfehlungen oder Auflagen des Sachverständigen..."
                  value={form.maengel}
                  onChange={e => setForm(f => ({ ...f, maengel: e.target.value }))}
                  rows={3}
                  data-testid="maengel-input"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            {/* ── Notizen ── */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notizen <span className="text-muted-foreground font-normal">(optional)</span></label>
              <textarea
                placeholder="Weitere Besonderheiten oder Hinweise..."
                value={form.notizen}
                onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))}
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* ── Dokument-Upload ── */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {form.entry_type === 'B' ? 'Prüfprotokoll' : 'Übernahmeschein'}
                <span className="text-muted-foreground font-normal ml-1">(optional · Foto oder PDF)</span>
              </label>
              {dokument ? (
                <div className="relative">
                  {dokumentType?.startsWith('image/') ? (
                    <img src={dokumentPreview} alt="Vorschau" className="w-full rounded-lg max-h-32 object-cover border" />
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg border">
                      <File className="h-7 w-7 text-primary shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{dokument.name}</p>
                        <p className="text-xs text-muted-foreground">{(dokument.size / 1024).toFixed(0)} KB</p>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => { setDokument(null); setDokumentPreview(null); setDokumentType(null); }}
                    className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  {editingId && existingDoc && (
                    <div className="flex items-center gap-2 p-2 mb-2 bg-muted/40 rounded-lg border text-xs text-muted-foreground" data-testid="existing-dokument-hint">
                      <File className="h-4 w-4 text-primary shrink-0" />
                      Vorhandenes Dokument bleibt erhalten – neues Hochladen ersetzt es.
                    </div>
                  )}
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-full border-2 border-dashed border-border rounded-xl p-4 flex items-center justify-center gap-3 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    data-testid="dokument-upload-btn"
                  >
                    <Camera className="h-6 w-6" />
                    <div className="text-left">
                      <p className="text-sm font-medium">{editingId && existingDoc ? 'Dokument ersetzen' : 'Foto / PDF hochladen'}</p>
                      <p className="text-xs">JPG, PNG oder PDF · max. 20 MB</p>
                    </div>
                  </button>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                onChange={handleFileChange}
                className="hidden"
                data-testid="dokument-file-input"
              />
            </div>
            {/* ── Unterschriften ── */}
            <div className="space-y-4 pt-2 border-t border-border">
              <label className="text-sm font-semibold text-foreground">Unterschriften</label>

              {/* Unterschrift ausfüllende Person – immer */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-1.5">
                    <PenLine className="h-3.5 w-3.5 text-muted-foreground" />
                    Unterschrift – ausfüllende Person
                    <span className="text-destructive">*</span>
                  </label>
                  <button type="button" onClick={() => sigPersonRef.current?.clear()}
                    className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1">
                    <RotateCcw className="h-3 w-3" /> Löschen
                  </button>
                </div>
                <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-gray-950">
                  <SignaturePad
                    ref={sigPersonRef}
                    canvasProps={{
                      className: 'w-full',
                      style: { height: '100px', touchAction: 'none' },
                    }}
                    penColor="#1a1a1a"
                    backgroundColor="transparent"
                  />
                </div>
                {editingId && existingSig.person ? (
                  <div className="text-xs text-muted-foreground" data-testid="existing-sig-person">
                    <span className="block mb-1">Vorhandene Unterschrift (bleibt erhalten – nur neu zeichnen zum Ersetzen):</span>
                    <img src={existingSig.person} alt="Vorhandene Unterschrift" className="border rounded bg-white max-h-12 object-contain" />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Mit Finger oder Stift unterschreiben</p>
                )}
              </div>

              {/* Unterschrift externe Firma – nur bei Fachkundigenprüfung (B) */}
              {form.entry_type === 'B' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium flex items-center gap-1.5">
                      <PenLine className="h-3.5 w-3.5 text-violet-600" />
                      Unterschrift – externe Firma / Sachverständiger
                    </label>
                    <button type="button" onClick={() => sigFirmaRef.current?.clear()}
                      className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1">
                      <RotateCcw className="h-3 w-3" /> Löschen
                    </button>
                  </div>
                  <div className="border-2 border-violet-200 dark:border-violet-800 rounded-xl overflow-hidden bg-white dark:bg-gray-950">
                    <SignaturePad
                      ref={sigFirmaRef}
                      canvasProps={{
                        className: 'w-full',
                        style: { height: '100px', touchAction: 'none' },
                      }}
                      penColor="#6d28d9"
                      backgroundColor="transparent"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Unterschrift des Sachverständigen ({form.performed_by || 'externe Firma'})</p>
                  {editingId && existingSig.firma && (
                    <div className="text-xs text-muted-foreground" data-testid="existing-sig-firma">
                      <span className="block mb-1">Vorhandene Unterschrift (bleibt erhalten – nur neu zeichnen zum Ersetzen):</span>
                      <img src={existingSig.firma} alt="Vorhandene Unterschrift Firma" className="border border-violet-200 rounded bg-white max-h-12 object-contain" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-eintrag-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>

    {/* ── Vollbild-Dokumenten-Viewer ─────────────────────────────────────── */}
    {viewerUrl && (
      <div
        className="fixed inset-0 z-[9999] bg-black flex flex-col"
        onClick={() => setViewerUrl(null)}
      >
        {/* X-Button – groß (56px), safe-area aware, immer erreichbar */}
        <div
          className="flex justify-end shrink-0"
          style={{
            paddingTop: 'max(20px, calc(env(safe-area-inset-top, 0px) + 12px))',
            paddingRight: 'max(16px, env(safe-area-inset-right, 16px))',
            paddingBottom: '8px',
            paddingLeft: '16px',
          }}
        >
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setViewerUrl(null); }}
            className="flex items-center justify-center rounded-full bg-white/95 active:bg-white text-black shadow-2xl"
            style={{
              width: 56, height: 56, minWidth: 56, minHeight: 56,
              touchAction: 'manipulation', cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
            aria-label="Schließen"
            data-testid="viewer-close-btn"
          >
            <X style={{ width: 30, height: 30, strokeWidth: 2.5 }} />
          </button>
        </div>

        {/* Bild – scrollbar bei großen Fotos */}
        <div
          className="flex-1 flex items-center justify-center overflow-auto"
          style={{ padding: '8px 16px' }}
          onClick={e => e.stopPropagation()}
        >
          <img
            src={viewerUrl}
            alt="Messprotokoll"
            className="max-w-full object-contain rounded-xl shadow-2xl"
            style={{ maxHeight: 'calc(100dvh - 120px)' }}
          />
        </div>

        {/* Hinweis unten mit safe-area */}
        <p
          className="text-center text-white/50 text-xs shrink-0"
          style={{
            paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))',
            paddingTop: '8px',
          }}
        >
          Außerhalb tippen oder X drücken zum Schließen
        </p>
      </div>
    )}
    </>
  );
}
